import { useMemo, useState } from 'react'
import { useAccount } from 'wagmi'
import { parseUnits } from 'viem'
import { T } from '@/lib/theme'
import { AmountSlider, Button, MaxLink, Panel, SectionLabel, ToggleButton } from '@/components/ui'
import { TokenGlyph } from '@/components/dashboard/TokenGlyph'
import { ADDRESSES } from '@/lib/contracts'
import savingsAbi from '@/lib/abis/SavingsVault.json'
import farmAbi from '@/lib/abis/HorizonEmissions.json'
import { useSavingsHUsd } from '@/hooks/useSavingsHUsd'
import { useAction } from '@/hooks/useAction'
import { fmt, fmtBalance, fmtBalanceWei, fmtUsd, fmtPct, validateAmount, weiToInputString } from '@/lib/utils'

// The Horizon-blue brand gradient, used for the rate bars (the panel's premium cue). Kept in the blue
// palette (was violet->cyan); mirrors the accent token range.
const BRAND = 'linear-gradient(90deg, #3D8BFF 0%, #9CC6FF 100%)'
const BAR_MAX = 0.30 // a rate bar reads full at 30% APY

/// The hUSD savings account (shUSD). Deposit hUSD, hold a yield-bearing share whose price rises
/// in hUSD as protocol revenue streams in, plus a time-boxed HORIZON bootstrap. Styled as the tiered
/// "holder benefits" card: layered near-black surfaces, gradient rate bars, pill tags, one
/// highlighted row.
export function SavingsPanel() {
  const { vault, refetch, notDeployed } = useSavingsHUsd()
  const { address: user } = useAccount()
  // Visible but greyed until hUSD is deployed (Mining-page pattern), rather than hiding the card.
  if (notDeployed) return <SavingsPlaceholder />

  return (
    <div style={{ marginTop: 40 }}>
      <SectionLabel>hUSD savings</SectionLabel>
      <div style={intro}>
        Deposit hUSD and earn automatically. Your balance grows in hUSD as protocol revenue flows
        in, with a launch HORIZON bonus on top. Withdraw whenever you like.
      </div>

      <Panel style={{ padding: 0, overflow: 'hidden' }}>
        <div style={grid}>
          <div style={leftCol}>
            <div style={eyebrow}>
              <TokenGlyph symbol="hUSD" size={40} />
              <span style={eyebrowText}>SAVINGS hUSD</span>
            </div>
            <div style={headline}>Hold hUSD.</div>
            <div style={{ ...headline, color: T.textDim }}>Earn while you hold.</div>

            <div style={pitch}>
              The savings rate is funded by the spread on hUSD borrowing, so you are paid from what
              borrowers already pay. The HORIZON bonus seeds the launch and tapers out.
            </div>

            <div style={pills}>
              <Pill>Spread-funded yield</Pill>
              <Pill>Withdraw anytime</Pill>
              <Pill>Auto-compounding</Pill>
            </div>

            <SavingsForm vault={vault} user={user} onTx={refetch} />
          </div>

          <div style={rightCol}>
            <div style={statHead}>
              <span style={statHeadLabel}>CURRENT RATE</span>
              <span style={statHeadLabel}>APY</span>
            </div>

            <RateBar label="Total" value={vault.totalApy} known={vault.hasHorizonPrice} highlight />
            <RateBar label="hUSD yield" value={vault.organicApy} known />
            <RateBar label="HORIZON bonus" value={vault.bootstrapApy} known={vault.hasHorizonPrice} />

            <div style={statDivider} />

            <div style={statRow}>
              <span style={statRowLabel}>Total deposits</span>
              <span className="mono" style={statRowValue}>{fmt(vault.totalAssets, 0)} hUSD</span>
            </div>
            <div style={statRow}>
              <span style={statRowLabel}>Your balance</span>
              <span className="mono" style={statRowValue}>
                {user ? `${fmt(vault.user.assets, 2)} hUSD` : '—'}
              </span>
            </div>
            <div style={statFoot}>
              Rates are variable and shown live. The hUSD figure tracks the current revenue stream;
              the HORIZON bonus is time-boxed. {termLabel(vault)}
            </div>
          </div>
        </div>
      </Panel>
    </div>
  )
}

/// One gradient rate bar. Width tracks the value against a 30% full-scale, the fill is the brand
/// gradient, and the total row is highlighted with a faint violet ring exactly as the active tier is
/// in the reference card.
function RateBar({ label, value, known, highlight }) {
  const pct = Math.max(0, Math.min(1, (value || 0) / BAR_MAX))
  return (
    <div style={highlight ? barRowActive : barRow}>
      <span style={{ ...barLabel, color: highlight ? T.text : T.textMuted }}>{label}</span>
      <div style={barTrack}>
        <div style={{ ...barFill, width: `${pct * 100}%` }} />
      </div>
      <span className="mono" style={{ ...barValue, color: highlight ? T.text : T.textMuted }}>
        {known ? fmtPct(value) : '—'}
      </span>
    </div>
  )
}

// Greyed, non-interactive stand-in shown while the savings vault is not deployed yet — keeps the
// savings pitch visible (Mining-page pattern) instead of hiding the whole card.
function SavingsPlaceholder() {
  return (
    <div style={{ marginTop: 40, opacity: 0.55 }}>
      <SectionLabel>hUSD savings</SectionLabel>
      <div style={intro}>
        Deposit hUSD and earn automatically. Your balance grows in hUSD as protocol revenue flows
        in, with a launch HORIZON bonus on top. Withdraw whenever you like.
      </div>
      <Panel style={{ padding: 0, overflow: 'hidden' }}>
        <div style={grid}>
          <div style={leftCol}>
            <div style={eyebrow}>
              <TokenGlyph symbol="hUSD" size={40} />
              <span style={eyebrowText}>SAVINGS hUSD</span>
            </div>
            <div style={headline}>Hold hUSD.</div>
            <div style={{ ...headline, color: T.textDim }}>Earn while you hold.</div>
            <div style={pitch}>
              The savings rate is funded by the spread on hUSD borrowing, so you are paid from what
              borrowers already pay. The HORIZON bonus seeds the launch and tapers out.
            </div>
            <div style={pills}>
              <Pill>Spread-funded yield</Pill>
              <Pill>Withdraw anytime</Pill>
              <Pill>Auto-compounding</Pill>
            </div>
          </div>
          <div style={rightCol}>
            <div style={{ minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.body, lineHeight: 1.6, padding: 24 }}>
              Savings opens at launch, once hUSD is deployed.
            </div>
          </div>
        </div>
      </Panel>
    </div>
  )
}

function SavingsForm({ vault, user, onTx }) {
  const action = useAction()
  const [mode, setMode] = useState('deposit')
  const [amount, setAmount] = useState('')
  const isDeposit = mode === 'deposit'

  const wei = useMemo(() => {
    try { return amount && !validateAmount(amount) ? parseUnits(amount, vault.decimals) : 0n } catch { return 0n }
  }, [amount, vault.decimals])

  const maxWei = isDeposit ? vault.user.walletWei : vault.user.assetsWei
  const closed = vault.depositsPaused

  return (
    <div style={{ marginTop: 22 }}>
      {vault.user.pendingHorizon > 0 && (
        <div style={pendingRow}>
          <span style={{ color: T.positive, fontFamily: T.fontM, fontSize: T.fs.caption }}>
            {fmtBalance(vault.user.pendingHorizon, 4)} HORIZON bonus earned
            {vault.user.pendingHorizonUsd != null ? ` · ≈ ${fmtUsd(vault.user.pendingHorizonUsd)}` : ''}
          </span>
          <Button variant="secondary" style={claimBtn} disabled={action.pending} onClick={claim}>
            {action.pending ? 'Working…' : 'Claim'}
          </Button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 6 }}>
        <ToggleButton active={isDeposit} onClick={() => { setMode('deposit'); setAmount('') }} disabled={closed}>Deposit</ToggleButton>
        <ToggleButton active={!isDeposit} onClick={() => { setMode('withdraw'); setAmount('') }}>Withdraw</ToggleButton>
      </div>

      <input
        type="text" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00"
        style={{ ...inputBox, marginTop: 12 }}
      />
      <div style={{ textAlign: 'right', marginTop: 4 }}>
        <span style={{ color: T.textDim, fontSize: T.fs.caption, fontFamily: T.fontM }}>
          {isDeposit ? 'Wallet' : 'Deposited'}: {user ? fmtBalanceWei(maxWei, vault.decimals) : '—'} hUSD
          {user && <MaxLink onClick={() => setAmount(weiToInputString(maxWei, vault.decimals))} />}
        </span>
      </div>
      <AmountSlider
        valueWei={wei} maxWei={maxWei}
        onChangeWei={(w) => setAmount(weiToInputString(w, vault.decimals))} disabled={!user}
      />

      <div style={{ marginTop: 14 }}>
        {!user ? (
          <Button disabled style={btnFull}>Connect wallet</Button>
        ) : isDeposit && closed ? (
          <Button disabled style={btnFull}>Deposits paused</Button>
        ) : (
          <Button onClick={submit} disabled={!wei || action.pending} style={btnFull}>
            {action.pending ? 'Working…' : isDeposit ? 'Deposit' : 'Withdraw'}
          </Button>
        )}
      </div>

      {action.error && (
        <div style={{ color: T.negative, marginTop: 10, fontSize: T.fs.caption }}>{String(action.error)}</div>
      )}
    </div>
  )

  async function submit() {
    const intent = isDeposit
      ? {
          pulls: [{ token: vault.asset, spender: vault.address, amount: wei }],
          calls: [{ address: vault.address, abi: savingsAbi, functionName: 'deposit', args: [wei, user] }],
          permit: { token: vault.asset, amount: wei, spender: vault.address,
            buildCall: (p, sig) => ({ address: vault.address, abi: savingsAbi, functionName: 'depositWithPermit', args: [p, sig, user] }) },
          successMsg: `Deposited ${amount} hUSD.`,
        }
      : { calls: [{ address: vault.address, abi: savingsAbi, functionName: 'withdraw', args: [wei, user, user] }], successMsg: `Withdrew ${amount} hUSD.` }
    const res = await action.run(intent)
    if (!res) return
    setAmount('')
    onTx?.()
  }

  async function claim() {
    const res = await action.run({ calls: [{ address: ADDRESSES.HORIZON_EMISSIONS, abi: farmAbi, functionName: 'claimVaultIncentive', args: [vault.address] }], successMsg: 'HORIZON claimed.' })
    if (res) onTx?.()
  }
}

/// The bonus term in words. Accrued HORIZON stays claimable after it ends — the stream stops, the
/// balance does not vanish — so the copy never reads as forfeiture.
function termLabel(vault) {
  if (!vault.endsAt) return 'The HORIZON bonus begins once its budget is set.'
  const endsMs = vault.endsAt * 1000
  const when = new Date(endsMs).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
  if (Date.now() >= endsMs) return `The HORIZON bonus ran until ${when}; the hUSD yield continues.`
  const days = Math.max(1, Math.ceil((endsMs - Date.now()) / 86_400_000))
  return `HORIZON bonus runs until ${when}, about ${days} day${days === 1 ? '' : 's'} left.`
}

function Pill({ children }) {
  return (
    <span style={pill}>
      <span style={pillCheck}>✓</span>
      {children}
    </span>
  )
}

// ─── styles ────────────────────────────────────────────────────────────────
const intro = {
  marginTop: 8, marginBottom: 20, color: T.textMuted, fontFamily: T.fontM,
  fontSize: T.fs.body, lineHeight: 1.6, maxWidth: 720,
}
const grid = {
  display: 'grid', gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 1fr)', gap: 0,
}
const leftCol = { padding: '32px 30px', minWidth: 0 }
const rightCol = {
  padding: '30px 28px', minWidth: 0,
  background: 'rgba(255,255,255,0.015)',
  borderLeft: `1px solid ${T.border}`,
}
const eyebrow = { display: 'flex', alignItems: 'center', gap: 10 }
const eyebrowText = {
  color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, fontWeight: 700, letterSpacing: '0.12em',
}
const headline = {
  color: T.text, fontFamily: T.fontD, fontSize: 34, fontWeight: 700, lineHeight: 1.08, marginTop: 18,
}
const pitch = {
  marginTop: 16, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.body, lineHeight: 1.65, maxWidth: 440,
}
const pills = { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 22 }
const pill = {
  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '7px 13px 7px 9px',
  borderRadius: 999, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
  color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.caption, whiteSpace: 'nowrap',
}
const pillCheck = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16,
  borderRadius: 999, background: 'rgba(91,158,255,0.18)', color: '#9CC6FF', fontSize: 10, fontWeight: 700,
}
const statHead = { display: 'flex', justifyContent: 'space-between', marginBottom: 14 }
const statHeadLabel = {
  color: T.textDim, fontFamily: T.fontM, fontSize: 10, fontWeight: 700, letterSpacing: '0.1em',
}
const barRow = {
  display: 'flex', alignItems: 'center', gap: 14, padding: '11px 12px', borderRadius: T.r.md,
}
const barRowActive = {
  ...barRow, background: 'rgba(91,158,255,0.06)',
  boxShadow: 'inset 0 0 0 1px rgba(91,158,255,0.4)',
}
const barLabel = { width: 108, flexShrink: 0, fontFamily: T.fontM, fontSize: T.fs.caption }
const barTrack = {
  flex: '1 1 auto', height: 8, borderRadius: 999, background: 'rgba(255,255,255,0.06)', overflow: 'hidden',
}
const barFill = { height: '100%', borderRadius: 999, background: BRAND }
const barValue = { width: 58, flexShrink: 0, textAlign: 'right', fontFamily: T.fontN, fontSize: T.fs.ui, fontWeight: 700 }
const statDivider = { height: 1, background: T.border, margin: '18px 0 14px' }
const statRow = { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '5px 12px' }
const statRowLabel = { color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }
const statRowValue = { color: T.text, fontFamily: T.fontN, fontSize: T.fs.body }
const statFoot = {
  marginTop: 16, padding: '0 12px', color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, lineHeight: 1.6,
}
const pendingRow = {
  marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
}
const claimBtn = { padding: '6px 14px', fontSize: T.fs.caption }
const btnFull = { width: '100%', padding: '12px 16px', fontSize: T.fs.body }
const inputBox = {
  width: '100%', background: T.bg2, border: `1px solid ${T.border}`, borderRadius: T.r.md,
  padding: '10px 12px', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.25)',
  color: T.text, fontFamily: T.fontN, fontSize: T.fs.ui, outline: 'none', boxSizing: 'border-box',
}
